import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import CreateScheduleModal from "./CreateScheduleModal";
import ShareChatModal from "../../../../Chat/components/ShareChatModal";
import useRunTask from "../../../hooks/useRunTask";
import ToolIcon from "../../../../Chat/components/WelcomeSections/ToolIcon";
import { useAppServices } from "../../../../../hook/services";
import { useUserInfo } from "../../../../../context/user";
import CreateWebhookModal from "../../../../../components/CreateWebhookModal";
import {
  createProviderSelectOption,
  DEFAULT_OUTPUT_OPTION,
  isChannelProvider,
} from "../utils";

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 42,
    borderColor: "#e2e8f0",
    boxShadow: "none",
    fontSize: 14,
    borderRadius: 8,
  }),
  menu: (base) => ({ ...base, zIndex: 10 }),
};

const PencilIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
    <g clipPath="url(#clip0_pencil)">
      <path d="M1.5 10.35L0 15L4.65 13.5L1.5 10.35Z" fill="#757575" />
      <path d="M9.87189 1.95381L2.55347 9.27223L5.7354 12.4542L13.0538 5.13574L9.87189 1.95381Z" fill="#757575" />
      <path d="M14.775 2.325L12.675 0.225C12.375 -0.075 11.925 -0.075 11.625 0.225L10.95 0.9L14.1 4.05L14.775 3.375C15.075 3.075 15.075 2.625 14.775 2.325Z" fill="#757575" />
    </g>
    <defs>
      <clipPath id="clip0_pencil">
        <rect width="15" height="15" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const TaskPlay = ({ size = 12 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="11" viewBox="0 0 10 11" fill="none">
    <path d="M9.47348 4.61182L1.61612 0.141102C0.977706 -0.221969 0 0.130361 0 1.02837V9.96766C0 10.7733 0.908508 11.2588 1.61612 10.8549L9.47348 6.38636C10.1744 5.98891 10.1766 5.00926 9.47348 4.61182Z" fill="#5C6CFF" />
  </svg>
)

const PlayIcon = ({ size = 12 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M6 0C2.68548 0 0 2.68548 0 6C0 9.31452 2.68548 12 6 12C9.31452 12 12 9.31452 12 6C12 2.68548 9.31452 0 6 0ZM8.79919 6.58065L4.54113 9.02419C4.15887 9.2371 3.67742 8.96371 3.67742 8.51613V3.48387C3.67742 3.03871 4.15645 2.7629 4.54113 2.97581L8.79919 5.56452C9.19597 5.7871 9.19597 6.36048 8.79919 6.58065Z" fill="#009951" />
  </svg>
);

const ShareIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
    <path d="M13.5471 5.88465L9.25017 2.17408C8.87405 1.84925 8.28125 2.11295 8.28125 2.61754V4.57196C4.35964 4.61685 1.25 5.40282 1.25 9.11927C1.25 10.6193 2.21633 12.1053 3.2845 12.8823C3.61782 13.1247 4.09287 12.8204 3.96997 12.4274C2.86294 8.88704 4.49504 7.94715 8.28125 7.89268V10.039C8.28125 10.5444 8.87451 10.8069 9.25017 10.4825L13.5471 6.77157C13.8174 6.53812 13.8178 6.11842 13.5471 5.88465Z" fill="#757575" />
  </svg>
);

const SlackLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M5 14a2 2 0 1 1-2-2h2v2Z" fill="#36C5F0" />
    <path d="M6 14a2 2 0 1 1 4 0v5a2 2 0 1 1-4 0v-5Z" fill="#36C5F0" />
    <path d="M10 5a2 2 0 1 1 2-2v2h-2Z" fill="#2EB67D" />
    <path d="M10 6a2 2 0 1 1 0 4H5a2 2 0 1 1 0-4h5Z" fill="#2EB67D" />
    <path d="M19 10a2 2 0 1 1 2 2h-2v-2Z" fill="#ECB22E" />
    <path d="M18 10a2 2 0 1 1-4 0V5a2 2 0 1 1 4 0v5Z" fill="#ECB22E" />
    <path d="M14 19a2 2 0 1 1-2 2v-2h2Z" fill="#E01E5A" />
    <path d="M14 18a2 2 0 1 1 0-4h5a2 2 0 1 1 0 4h-5Z" fill="#E01E5A" />
  </svg>
);

const DriveLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M7.5 2.5h9L23 14.5l-3.5 6h-9L4 14.5 7.5 2.5Z" fill="#FFC107" />
    <path d="M4 14.5 7.5 2.5 12 10l-4.5 8L4 14.5Z" fill="#1E88E5" />
    <path d="m23 14.5-3.5 6H7.5L12 13l11 1.5Z" fill="#4CAF50" />
  </svg>
);

const Pill = ({ active, disabled, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center gap-1.5 px-4 h-8 rounded-full text-[13px] border transition-colors ${active
      ? "!border-[#2563eb] text-[#1E1E1E] bg-[#F4F5FF]"
      : " !border-[#F5F5F5] text-[#475569] !bg-[#F5F5F5] "
      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
  >
    {children}
  </button>
);

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return null;
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

const useRelativeTime = (timestamp) => {
  const [, force] = useState(0);
  useEffect(() => {
    if (!timestamp) return undefined;
    const interval = setInterval(() => force((tick) => tick + 1), 30000);
    return () => clearInterval(interval);
  }, [timestamp]);
  return formatRelativeTime(timestamp);
};

const useTick = (intervalMs) => {
  const [, force] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => force((tick) => tick + 1), intervalMs);
    return () => clearInterval(interval);
  }, [intervalMs]);
};

const FULL_DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const ORDINAL_INDEX = {
  "1st": 1,
  "2nd": 2,
  "3rd": 3,
  "4th": 4,
  "5th": 5,
  Last: -1,
};

const parseTimeParts = (time) => {
  if (!time || typeof time !== "string") return { hours: 9, minutes: 0 };
  const [hh, mm] = time.split(":");
  return {
    hours: Math.max(0, Math.min(23, Number(hh) || 0)),
    minutes: Math.max(0, Math.min(59, Number(mm) || 0)),
  };
};

const setTimeOnDate = (date, time) => {
  const { hours, minutes } = parseTimeParts(time);
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const computeOrdinalWeekdayInMonth = (year, month, ordinal, weekdayIndex) => {
  if (ordinal === -1) {
    const lastOfMonth = new Date(year, month + 1, 0);
    const offset = (lastOfMonth.getDay() - weekdayIndex + 7) % 7;
    return new Date(year, month, lastOfMonth.getDate() - offset);
  }
  const firstOfMonth = new Date(year, month, 1);
  const offset = (weekdayIndex - firstOfMonth.getDay() + 7) % 7;
  const day = 1 + offset + (ordinal - 1) * 7;
  const result = new Date(year, month, day);
  return result.getMonth() === month ? result : null;
};

const computeNextRun = (config) => {
  if (!config) return null;
  const {
    repeat,
    every = 1,
    weekDays,
    monthlyMode,
    dayOfMonth = 1,
    weekOrdinal = "1st",
    weekdayName = "Monday",
    yearMonth = "January",
    time = "09:00",
  } = config;

  const now = new Date();

  if (repeat === "daily") {
    const candidate = setTimeOnDate(new Date(now), time);
    if (candidate <= now) candidate.setDate(candidate.getDate() + 1);
    return candidate;
  }

  if (repeat === "weekly") {
    const days = (weekDays || []).map((day) => FULL_DAY_NAMES.indexOf(day)).filter((index) => index >= 0);
    if (!days.length) return null;
    for (let offset = 0; offset < 14; offset += 1) {
      const candidate = setTimeOnDate(new Date(now), time);
      candidate.setDate(candidate.getDate() + offset);
      if (days.includes(candidate.getDay()) && candidate > now) return candidate;
    }
    return null;
  }

  if (repeat === "monthly") {
    for (let monthOffset = 0; monthOffset < 24; monthOffset += 1) {
      const baseYear = now.getFullYear();
      const baseMonth = now.getMonth() + monthOffset;
      let candidate;
      if (monthlyMode === "dayOfWeek") {
        const ordinalValue = ORDINAL_INDEX[weekOrdinal] ?? 1;
        const weekdayIndex = FULL_DAY_NAMES.indexOf(weekdayName);
        candidate = computeOrdinalWeekdayInMonth(
          baseYear,
          baseMonth,
          ordinalValue,
          weekdayIndex >= 0 ? weekdayIndex : 1,
        );
        if (!candidate) continue;
      } else {
        candidate = new Date(baseYear, baseMonth, dayOfMonth);
        if (candidate.getMonth() !== ((baseMonth % 12) + 12) % 12) continue;
      }
      setTimeOnDate(candidate, time);
      if (candidate > now) return candidate;
    }
    return null;
  }

  if (repeat === "yearly") {
    const targetMonth = MONTH_NAMES.indexOf(yearMonth);
    if (targetMonth < 0) return null;
    for (let yearOffset = 0; yearOffset < 5; yearOffset += 1) {
      const year = now.getFullYear() + yearOffset;
      let candidate;
      if (monthlyMode === "dayOfWeek") {
        const ordinalValue = ORDINAL_INDEX[weekOrdinal] ?? 1;
        const weekdayIndex = FULL_DAY_NAMES.indexOf(weekdayName);
        candidate = computeOrdinalWeekdayInMonth(
          year,
          targetMonth,
          ordinalValue,
          weekdayIndex >= 0 ? weekdayIndex : 1,
        );
        if (!candidate) continue;
      } else {
        candidate = new Date(year, targetMonth, dayOfMonth);
        if (candidate.getMonth() !== targetMonth) continue;
      }
      setTimeOnDate(candidate, time);
      if (candidate > now) return candidate;
    }
    return null;
  }

  return null;
};

const formatClockTime = (date) => {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const meridiem = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}${meridiem}`;
};

const formatNextRun = (date) => {
  if (!date) return null;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.round(
    (startOfDate.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24),
  );
  const time = formatClockTime(date);
  if (dayDiff === 0) return `Today ${time}`;
  if (dayDiff === 1) return `Tomorrow ${time}`;
  if (dayDiff > 1 && dayDiff < 7) return `${FULL_DAY_NAMES[date.getDay()]} ${time}`;
  return `${SHORT_MONTHS[date.getMonth()]} ${date.getDate()} ${time}`;
};

const PromptDetailsForm = ({
  isEdit,
  promptName,
  promptNameError,
  onPromptNameChange,
  isScheduleActive,
  outputOption,
  requiredIntegrations,
  channelList,
  slackChannel,
  onOutputOptionChange,
  onSlackChannelChange,
  onChannelSearchInputChange,
  onChannelMenuScrollToBottom,
  loadingChannels,
  loadingMoreChannels,
  channelSearchQuery,
  scheduleFrequency,
  scheduleTime,
  scheduleDate,
  scheduleRunCount,
  selectedWeekDays,
  onScheduleFrequencyChange,
  onScheduleTimeChange,
  onScheduleDateChange,
  onScheduleRunCountChange,
  onSelectedWeekDaysChange,
  promptContent,
  promptContentError,
  onPromptContentChange,
  promptSetAs,
  onPromptSetAsChange,
  estimatedTime,
  onEstimatedTimeChange,
  processing,
  onSubmit,
  onDeleteClick,
  lastSavedAt,
  editadata,
  getUpdatePromptsData,
  selectedWebhookId,
  onSelectedWebhookIdChange,
}) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const { runTask, running: runningTask } = useRunTask();

  const handleRunNow = () => {
    if (!isEdit) return;
    runTask(editadata);
  };
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleConfig, setScheduleConfig] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);

  useEffect(() => {
    if (
      !scheduleFrequency ||
      scheduleFrequency === "none" ||
      scheduleFrequency === "webhook"
    ) {
      return;
    }
    setScheduleConfig({
      repeat: scheduleFrequency,
      weekDays: Array.isArray(selectedWeekDays) ? selectedWeekDays : [],
      scheduleDate: scheduleDate || "",
      runCount: scheduleRunCount || 1,
      time: scheduleTime || "09:00",
    });
  }, [scheduleFrequency, scheduleTime, scheduleDate, scheduleRunCount, selectedWeekDays]);
  const nameInputRef = useRef(null);
  const quillRef = useRef(null);
  const lastPastedHtmlRef = useRef(null);
  const savedAgo = useRelativeTime(lastSavedAt);

  useEffect(() => {
    const el = quillRef.current;
    if (!el) return;
    const html = promptContent || "";
    if (lastPastedHtmlRef.current === html) return;
    if (el.innerHTML === html) {
      lastPastedHtmlRef.current = html;
      return;
    }
    el.innerHTML = html;
    lastPastedHtmlRef.current = html;
  }, [promptContent]);


  const Service = useAppServices();
  const webhookUserInfo = useUserInfo();
  const webhookUser = webhookUserInfo[0];
  const webhookLocation = webhookUserInfo[4];
  const webhookUserId = webhookUser?._id;
  const webhookAccountId = webhookLocation?.id;

  const [webhooks, setWebhooks] = useState([]);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [webhookError, setWebhookError] = useState("");
  const setSelectedWebhookId = onSelectedWebhookIdChange || (() => { });
  const [showCreateWebhook, setShowCreateWebhook] = useState(false);
  const [showWebhookMenu, setShowWebhookMenu] = useState(false);
  const [newWebhookName, setNewWebhookName] = useState("");
  const [creatingWebhook, setCreatingWebhook] = useState(false);
  const webhookMenuRef = useRef(null);

  useEffect(() => {
    if (!showWebhookMenu) return undefined;
    const handleDocumentClick = (event) => {
      if (
        webhookMenuRef.current &&
        !webhookMenuRef.current.contains(event.target)
      ) {
        setShowWebhookMenu(false);
      }
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, [showWebhookMenu]);

  const loadWebhooks = useCallback(async () => {
    if (!webhookUserId) return;
    setWebhooksLoading(true);
    setWebhookError("");
    const { response, error } = await Service.webhook_config.list({
      query: `user_id=${webhookUserId}&account_id=${webhookAccountId}`,
    });
    setWebhooksLoading(false);
    if (error) {
      setWebhookError(error?.message || "Failed to load webhooks");
      return;
    }
    setWebhooks(response?.data?.webhooks || []);

  }, [webhookUserId]);

  useEffect(() => {
    loadWebhooks();
  }, [webhookUserId]);

  const selectedWebhook = webhooks.find(
    (hook) => hook.source_id === selectedWebhookId,
  );

  const handleCreateWebhook = async () => {
    const trimmed = newWebhookName.trim();
    if (!trimmed || !webhookUserId || creatingWebhook) return;
    setCreatingWebhook(true);
    setWebhookError("");
    const { response, error } = await Service.webhook_config.create({
      payload: {
        user_id: webhookUserId,
        account_id: webhookAccountId,
        webhook_name: trimmed,
      },
    });
    setCreatingWebhook(false);
    if (error) {
      setWebhookError(error?.message || "Failed to create webhook");
      return;
    }
    const created = response?.data;
    if (created) {
      setWebhooks((current) => [
        {
          source_id: created.source_id,
          webhook_name: created.webhook_name,
          active: created.active,
          configured_at: created.configured_at,
        },
        ...current,
      ]);
      setSelectedWebhookId(created.source_id);
    }
    setNewWebhookName("");
    setShowCreateWebhook(false);
    setShowWebhookMenu(false);
  };

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select?.();
    }
  }, [isEditingName]);

  useEffect(() => {
    if (!isEdit) return;
    console.log("[PromptDetailsForm] Edit data:", {
      editadata,
      promptName,
      promptContent,
      promptSetAs,
      outputOption,
      slackChannel,
      scheduleFrequency,
      scheduleTime,
      scheduleDate,
      scheduleRunCount,
      selectedWeekDays,
      isScheduleActive,
      lastSavedAt,
    });
  }, [
    isEdit,
    editadata,
    promptName,
    promptContent,
    promptSetAs,
    outputOption,
    slackChannel,
    scheduleFrequency,
    scheduleTime,
    scheduleDate,
    scheduleRunCount,
    selectedWeekDays,
    isScheduleActive,
    lastSavedAt,
  ]);

  const handleNameKeyDown = (event) => {
    if (event.key === "Enter" || event.key === "Escape") {
      event.preventDefault();
      setIsEditingName(false);
    }
  };

  const showChannelPicker = isChannelProvider(outputOption?.value);
  const selectedChannel = useMemo(() => {
    return channelList.find(
      (channel) => channel.channel_id === slackChannel?.value,
    )
  }, [slackChannel, channelList])

  const selectedChannelOption = selectedChannel
    ? {
      value: selectedChannel.channel_id,
      label: selectedChannel.channel_name,
    }
    : slackChannel?.value ? slackChannel : null;

  const channelOptions = useMemo(() => {
    const baseOptions = channelList.map((channel) => ({
      value: channel.channel_id,
      label: channel.channel_name,
    }));

    if (
      selectedChannelOption?.value &&
      selectedChannelOption?.label &&
      !baseOptions.some((option) => option.value === selectedChannelOption.value)
    ) {
      return [selectedChannelOption, ...baseOptions];
    }

    return baseOptions;
  }, [channelList, selectedChannelOption]);

  const providerOptions = useMemo(() => {
    const fromIntegrations = [...requiredIntegrations]
      .sort((a, b) =>
        (a.provider_name || "").localeCompare(b.provider_name || ""),
      )
      .map(createProviderSelectOption);

    const opts = [...fromIntegrations, DEFAULT_OUTPUT_OPTION];

    if (
      outputOption?.value &&
      outputOption.value !== DEFAULT_OUTPUT_OPTION.value &&
      !opts.some((opt) => opt.value === outputOption.value)
    ) {
      opts.unshift(createProviderSelectOption({ provider_id: outputOption.value }));
    }

    return opts;
  }, [requiredIntegrations, outputOption]);

  const triggerType =
    scheduleFrequency === "webhook"
      ? "webhook"
      : !scheduleFrequency || scheduleFrequency === "none"
        ? "manual"
        : "scheduled";

  const handleTriggerChange = (type) => {
    if (type === "manual") {
      onScheduleFrequencyChange("none");
    } else if (type === "scheduled" && triggerType !== "scheduled") {
      onScheduleFrequencyChange("weekly");
    } else if (type === "webhook") {
      onScheduleFrequencyChange("webhook");
    }
  };

  const formatScheduleSummary = () => {
    if (!scheduleConfig) return null;
    const { repeat, weekDays, runCount, scheduleDate: cfgDate, time } = scheduleConfig;
    const displayTime = time || "09:00";

    if (repeat === "daily") {
      return `Daily | ${displayTime}`;
    }
    if (repeat === "weekly") {
      if (!weekDays?.length) return `Add Schedule`;
      const oneDay = weekDays.length === 1 ? `Every ${weekDays[0]}` : weekDays.map((d) => d.slice(0, 3)).join(", ");
      const prefix = runCount > 1 ? `Every ${runCount} weeks: ` : "";
      return `${prefix}${oneDay} | ${displayTime}`;
    }
    if (repeat === "monthly") {
      if (!cfgDate) return "Add Schedule";
      const prefix = runCount > 1 ? `Every ${runCount} months on ` : "Monthly on ";
      return `${prefix}${cfgDate} | ${displayTime}`;
    }
    if (repeat === "yearly") {
      if (!cfgDate) return "Add Schedule";
      const prefix = runCount > 1 ? `Every ${runCount} years on ` : "Yearly on ";
      return `${prefix}${cfgDate} | ${displayTime}`;
    }
    return null;
  };

  const handleScheduleCreate = (config) => {
    setScheduleConfig(config);
    onScheduleFrequencyChange?.(config.repeat);
    onScheduleTimeChange?.(config.time);
    onScheduleDateChange?.(config.scheduleDate || "");
    onScheduleRunCountChange?.(config.runCount || 1);
    onSelectedWeekDaysChange?.(config.weekDays || []);
    setShowScheduleModal(false);
  };

  const scheduleSummary = formatScheduleSummary();
  useTick(60000);
  const nextRunLabel = formatNextRun(computeNextRun(scheduleConfig));

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap max-w-[500px]">
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <input
                  ref={nameInputRef}
                  type="text"
                  value={promptName}
                  onChange={(event) => onPromptNameChange(event.target.value)}
                  onBlur={() => setIsEditingName(false)}
                  onKeyDown={handleNameKeyDown}
                  placeholder="Untitled Task"
                  className="text-[18px] font-bold bg-transparent outline-none !border-b !border-[#D9D9D9] p-0"
                  style={{ minWidth: 0, flex: "0 1 auto", maxWidth: "100%", fontFamily: '"JetBrains Mono", monospace' }}
                />

              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsEditingName(true)}
                className="text-[18px] font-bold bg-transparent outline-none border-none p-0 text-left cursor-text hover:opacity-90"
                style={{ minWidth: 0, maxWidth: "100%", fontFamily: '"JetBrains Mono", monospace' }}
                title="Click to edit"
              >
                {promptName || (
                  <span className="text-[#94a3b8] font-normal">
                    Untitled Task
                  </span>
                )}
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsEditingName(true)}
              className="text-[#94a3b8] hover:text-black"
              aria-label="Edit name"
            >
              <PencilIcon />
            </button>
            {savedAgo && (
              <span className="text-[12px] text-[#757575] flex items-center gap-1 whitespace-nowrap">
                <span className="text-[#757575]">
                  <CheckIcon />
                </span>
                Saved {savedAgo}
              </span>
            )}
          </div>
          <p className="text-[14px] text-[#1E1E1E] mt-1">
            Summary, decisions, and action owners
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => {
              if (!isEdit) return;
              setShowShareModal(true);
            }}
            disabled={!isEdit}
            className={`w-9 h-11 flex items-center justify-center border !border-[#D9D9D9] rounded-md text-[#64748b] hover:bg-[#D9D9D9] ${!isEdit ? "opacity-50 cursor-not-allowed" : ""
              }`}
            aria-label="Share"
            title={isEdit ? "Share task" : "Save the task before sharing"}
          >
            <ShareIcon />
          </button>
          <button
            type="button"
            onClick={handleRunNow}
            disabled={!isEdit || runningTask}
            title={isEdit ? "Run task now" : "Save the task before running"}
            className={`flex items-center gap-2 px-4 h-9 text-[#2563eb] hover:text-[#1d4ed8] rounded-md text-[13px] font-medium ${!isEdit || runningTask ? "opacity-50 cursor-not-allowed" : ""
              }`}
          >
            {<TaskPlay />}
            {runningTask ? "Running..." : "Run Now"}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={processing}
            title={isEdit ? "Save changes" : "Create task"}
            className={`flex items-center gap-2 px-4 h-9 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-md text-[13px] font-medium ${processing ? "opacity-60 cursor-not-allowed" : ""
              }`}
          >
            {processing ? "Saving..." : isEdit ? "Save" : "Create"}
          </button>
        </div>
      </div>
      {promptNameError && (
        <small className="text-red-500 block mt-1">{promptNameError}</small>
      )}

      <div className="border-t border-[#e2e8f0] my-4" />

      {/* Estimated Time + Tools */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex gap-4">
          <div>
            <div className="text-[14px] font-medium text-[#757575]">Estimated Time Saved</div>
            <p className="text-[12px] text-[#757575]">Based on similar tasks</p>
          </div>
          <div className=" flex items-center gap-2">
            <input
              type="number"
              min={0}
              value={estimatedTime ?? 1}
              onChange={(event) => onEstimatedTimeChange?.(event.target.value)}
              className="w-14 h-8 border !border-[#D9D9D9] rounded-md px-2 text-[14px] outline-none focus:!border-[#D9D9D9]"
            />
            <span className="text-[14px] text-[#757575]">hours</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[14px] font-medium mb-2 text-[#757575]">Tools</div>
          <div className="flex items-center justify-end gap-2">
            {outputOption?.value &&
              outputOption.value.toLowerCase() !== "chatui" ? (
              <ToolIcon type={outputOption.value} />
            ) : (
              <span className="text-[12px] text-[#94a3b8]">None</span>
            )}
          </div>
        </div>
      </div>

      {/* Task Prompt */}
      <div className="mb-5">
        <label className="text-[14px] text-[#757575] mb-1.5 block">Task Prompt</label>
        <div className="prompt-quill-wrapper">
          <div
            ref={quillRef}
            contentEditable
            suppressContentEditableWarning
            className="prompt-html-editor ql-editor max-h-[150px] overflow-y-auto"
            data-placeholder="Describe what this task should do..."
            onInput={(event) => {
              const html = event.currentTarget.innerHTML;
              lastPastedHtmlRef.current = html;
              onPromptContentChange?.(html);
            }}
            onBlur={(event) => {
              const html = event.currentTarget.innerHTML;
              lastPastedHtmlRef.current = html;
              onPromptContentChange?.(html);
            }}
          />
        </div>
        {promptContentError && (
          <small className="text-red-500">{promptContentError}</small>
        )}
      </div>

      {/* Output Type + Deliver To */}
      <div className="grid grid-cols-2 gap-4 mb-5">
        <div>
          <label className="text-[14px] text-[#757575] font-medium mb-2 block">
            Output Type
          </label>
          <Select
            placeholder="Select output"
            value={showChannelPicker ? outputOption : DEFAULT_OUTPUT_OPTION}
            onChange={onOutputOptionChange}
            options={providerOptions}
            styles={selectStyles}
            isSearchable={false}
          />
        </div>
        <div>
          <label className="text-[14px] text-[#757575] font-medium mb-2 block">
            Deliver To
          </label>
          <Select
            placeholder={showChannelPicker ? "Search a channel" : "—"}
            value={selectedChannelOption}
            onChange={onSlackChannelChange}
            options={channelOptions}
            onInputChange={onChannelSearchInputChange}
            onMenuScrollToBottom={onChannelMenuScrollToBottom}
            isLoading={loadingChannels || loadingMoreChannels}
            filterOption={null}
            isDisabled={!showChannelPicker}
            styles={selectStyles}
            noOptionsMessage={() =>
              loadingChannels
                ? "Loading channels..."
                : channelSearchQuery
                  ? "No channels found"
                  : "No channels available"
            }
          />
        </div>
      </div>

      {/* Trigger */}
      <div className="mb-5">
        <label className="text-[14px] text-[#757575] font-medium mb-2 block">Trigger</label>
        <div className="flex items-center gap-2">
          <Pill
            active={triggerType === "manual"}
            onClick={() => handleTriggerChange("manual")}
          >
            Manual
          </Pill>
          <Pill
            active={triggerType === "scheduled"}
            onClick={() => handleTriggerChange("scheduled")}
          >
            Scheduled
          </Pill>
          <Pill
            active={triggerType === "webhook"}
            onClick={() => handleTriggerChange("webhook")}
          >
            Webhook
          </Pill>
        </div>
      </div>

      {/* Webhook dropdown */}
      {triggerType === "webhook" && (
        <div className="mb-5">
          <label className="text-[14px] text-[#757575] font-medium mb-2 block">Webhook</label>
          <div className="relative" ref={webhookMenuRef}>
            <button
              type="button"
              onClick={() => setShowWebhookMenu((value) => !value)}
              className="w-full h-11 border !border-[#D9D9D9] max-w-[300px] rounded-md px-3 flex items-center justify-between text-[14px] bg-white"
            >
              <span
                className={
                  selectedWebhook
                    ? "text-[#0f172a]"
                    : "text-[#2563eb]"
                }
              >
                {selectedWebhook ? selectedWebhook.webhook_name : "+ Create new"}
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[#94a3b8]"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {showWebhookMenu && (
              <div className="absolute left-0 right-0 mt-1 border !border-[#D9D9D9] max-w-[300px] rounded-md bg-white shadow-md z-10 overflow-hidden">
                {webhooksLoading ? (
                  <div className="px-3 h-10 flex items-center text-[14px] text-[#757575]">
                    Loading webhooks...
                  </div>
                ) : (
                  webhooks.map((hook) => (
                    <button
                      key={hook.source_id}
                      type="button"
                      onClick={() => {
                        setSelectedWebhookId(hook.source_id);
                        setShowWebhookMenu(false);
                      }}
                      className={`w-full text-left px-3 h-10 text-[14px] hover:bg-[#f8fafc] ${hook.source_id === selectedWebhookId
                        ? "bg-[#f1f5f9] text-[#0f172a] font-medium"
                        : "text-[#0f172a]"
                        }`}
                    >
                      {hook.webhook_name}
                    </button>
                  ))
                )}
                <button
                  type="button"
                  onClick={() => {
                    setShowWebhookMenu(false);
                    setShowCreateWebhook(true);
                  }}
                  className="w-full text-left px-3 h-10 text-[14px] text-[#2563eb] hover:bg-[#f8fafc] border-t !border-[#D9D9D9]"
                >
                  + Create new
                </button>
              </div>
            )}
          </div>
          {webhookError && (
            <small className="text-red-500 block mt-1">{webhookError}</small>
          )}
        </div>
      )}

      <div className="border-t border-[#e2e8f0] mb-5" />

      {/* Schedule */}
      {triggerType === "scheduled" && (
        <>
          <div className="flex items-start justify-between gap-4 mb-5">
            <div className="flex-1">
              <div className="text-[13px] font-medium mb-2">Schedule</div>
              {scheduleSummary ? (
                <div className="flex items-center gap-2">
                  {scheduleSummary !== "Add Schedule" && (
                    <span
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-white`}
                    >
                      <PlayIcon size={9} />
                    </span>
                  )}
                  {scheduleSummary === "Add Schedule" ? (
                    <button
                      type="button"
                      onClick={() => setShowScheduleModal(true)}
                      className="text-[14px] cursor-pointer bg-transparent border-0 p-0"
                      style={{ color: "#5C6CFF" }}
                    >
                      {scheduleSummary}
                    </button>
                  ) : (
                    <>
                      <span className="text-[14px]">{scheduleSummary}</span>
                      <button
                        type="button"
                        onClick={() => setShowScheduleModal(true)}
                        className="text-[#94a3b8] hover:text-black"
                        aria-label="Edit schedule"
                      >
                        <PencilIcon />
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowScheduleModal(true)}
                  className="text-[14px] text-[#2563eb] hover:underline"
                >
                  Add Schedule
                </button>
              )}
            </div>
            {scheduleSummary && (
              <div className="text-right">
                <div className="text-[13px]">
                  Next Run:{" "}
                  <span className="font-medium">
                    {nextRunLabel || "—"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setScheduleConfig(null);
                    onScheduleTimeChange?.("");
                    onSelectedWeekDaysChange?.([]);
                    onScheduleDateChange?.("");
                  }}
                  className="text-[12px] text-[#94a3b8] hover:text-[#ef4444] mt-1"
                >
                  Delete
                </button>
              </div>
            )}
          </div>

          <div className="border-t border-[#e2e8f0] mb-5" />
        </>
      )}

      <CreateScheduleModal
        open={showScheduleModal}
        initial={scheduleConfig}
        onClose={() => setShowScheduleModal(false)}
        onCreate={handleScheduleCreate}
      />

      <ShareChatModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        contact={editadata}
        shareType="task"
        resourceTitle={editadata?.title || promptName}
        onSuccess={getUpdatePromptsData}
      />

      <div className="grid grid-cols-2 gap-6 mb-6">
        <div>
          <div className="text-[14px] font-medium mb-1 text-[#757575]">Privacy</div>
          <p className="text-[12px] text-[#94a3b8] mb-3">
            {
              promptSetAs === "shared" ? "This task is visible to your teammates and can be shared." : "This task is only visible to you. Teammates can't see or edit it."
            }
          </p>
          <div className="flex items-center gap-2">
            <Pill
              active={promptSetAs === "shared"}
              onClick={() => onPromptSetAsChange("shared")}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="7" viewBox="0 0 10 7" fill="none">
                <path d="M5 1.45833C4.81599 1.46135 4.63335 1.49205 4.45764 1.54948C4.53892 1.69956 4.58219 1.86902 4.58333 2.04167C4.58333 2.17572 4.55819 2.30847 4.50933 2.43232C4.46047 2.55618 4.38885 2.66871 4.29857 2.7635C4.2083 2.8583 4.10112 2.93349 3.98316 2.98479C3.86521 3.0361 3.73878 3.0625 3.61111 3.0625C3.44668 3.0613 3.2853 3.01586 3.14236 2.93052C3.02959 3.34119 3.04273 3.7787 3.17993 4.18109C3.31713 4.58347 3.57142 4.93033 3.9068 5.17253C4.24217 5.41474 4.64162 5.54 5.04857 5.53059C5.45552 5.52118 5.84933 5.37756 6.17422 5.12008C6.49911 4.8626 6.73861 4.50432 6.8588 4.09598C6.97898 3.68764 6.97376 3.24994 6.84387 2.84489C6.71399 2.43984 6.46601 2.08797 6.13508 1.83911C5.80415 1.59026 5.40704 1.45704 5 1.45833ZM9.93959 3.23385C8.9981 1.30503 7.13421 0 5 0C2.86579 0 1.00138 1.30594 0.0604073 3.23404C0.0206923 3.31652 0 3.40766 0 3.50009C0 3.59252 0.0206923 3.68366 0.0604073 3.76615C1.0019 5.69497 2.86579 7 5 7C7.13421 7 8.99862 5.69406 9.93959 3.76596C9.97931 3.68347 10 3.59234 10 3.49991C10 3.40748 9.97931 3.31634 9.93959 3.23385ZM5 6.125C3.28732 6.125 1.71718 5.1224 0.869263 3.5C1.71718 1.8776 3.28715 0.875 5 0.875C6.71285 0.875 8.28282 1.8776 9.13074 3.5C8.28299 5.1224 6.71285 6.125 5 6.125Z" fill="#5C6CFF" />
              </svg>
              Visible to team
            </Pill>
            <Pill
              active={promptSetAs === "personal"}
              onClick={() => onPromptSetAsChange("personal")}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="8" viewBox="0 0 11 8" fill="none">
                <path d="M10.8967 7.35938L0.618899 0.0548438C0.590709 0.0343248 0.558349 0.0190545 0.523667 0.00990492C0.488986 0.000755309 0.452661 -0.00209446 0.416768 0.00151832C0.380875 0.0051311 0.346116 0.0151357 0.314476 0.0309608C0.282836 0.046786 0.254934 0.0681218 0.232364 0.0937501L0.0604942 0.288906C0.037871 0.314543 0.0210305 0.343984 0.0109362 0.375545C0.000842013 0.407105 -0.00230776 0.440165 0.00166716 0.472833C0.00564208 0.505502 0.0166637 0.537137 0.0341013 0.565928C0.0515388 0.59472 0.0750501 0.620103 0.10329 0.640625L10.3811 7.94516C10.4093 7.96568 10.4417 7.98095 10.4763 7.9901C10.511 7.99924 10.5473 8.00209 10.5832 7.99848C10.6191 7.99487 10.6539 7.98486 10.6855 7.96904C10.7172 7.95321 10.7451 7.93188 10.7676 7.90625L10.9395 7.71109C10.9621 7.68546 10.979 7.65602 10.9891 7.62446C10.9992 7.5929 11.0023 7.55983 10.9983 7.52717C10.9944 7.4945 10.9833 7.46286 10.9659 7.43407C10.9485 7.40528 10.9249 7.3799 10.8967 7.35938ZM5.10109 2.28859L7.41772 3.93516C7.37957 2.99859 6.53947 2.25 5.5 2.25C5.36592 2.25023 5.23222 2.26316 5.10109 2.28859ZM5.89891 5.71156L3.58228 4.065C3.6206 5.00141 4.4607 5.75 5.5 5.75C5.63407 5.74974 5.76776 5.73686 5.89891 5.71156ZM5.5 1.75C7.1955 1.75 8.74988 2.60938 9.5893 4C9.38353 4.34225 9.12929 4.65848 8.83307 4.94063L9.48171 5.40156C9.84284 5.05034 10.1488 4.6554 10.3909 4.22797C10.4302 4.15726 10.4507 4.07915 10.4507 3.99992C10.4507 3.9207 10.4302 3.84258 10.3909 3.77187C9.45799 2.11859 7.61279 1 5.5 1C4.86924 1 4.26752 1.10938 3.70173 1.29391L4.49937 1.86094C4.8249 1.79375 5.15832 1.75 5.5 1.75ZM5.5 6.25C3.8045 6.25 2.25029 5.39062 1.4107 4C1.61674 3.65776 1.87127 3.34157 2.16779 3.05953L1.51915 2.59859C1.15808 2.94976 0.852174 3.34465 0.610134 3.77203C0.570817 3.84274 0.550332 3.92085 0.550332 4.00008C0.550332 4.0793 0.570817 4.15742 0.610134 4.22813C1.54218 5.88141 3.38738 7 5.5 7C6.13076 7 6.73248 6.88984 7.29827 6.70609L6.50063 6.13922C6.1751 6.20625 5.84185 6.25 5.5 6.25Z" fill="#757575" />
              </svg>
              Only me
            </Pill>
          </div>
        </div>
        <div>
          {(() => {
            const sharedUsers = (editadata?.userId || []).filter(
              (user) => user?.type === "shared",
            );
            return (
              <>
                <div className="text-[14px] text-[#757575] font-medium mb-1">
                  Shared with ({sharedUsers.length})
                </div>
                <p className="text-[12px] text-[#757575] mb-2">
                  Sharing creates a copy of this task
                </p>
                {sharedUsers.length > 0 ? (
                  <ul className="text-[14px] text-[#0f172a] space-y-1 capitalize">
                    {sharedUsers.map((user) => (
                      <li key={user?.id || user?._id || user?.username}>
                        {user?.username}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[14px] text-[#757575] italic">
                    Not shared yet
                  </p>
                )}
                <div className="flex text-[1E1E1E] items-center gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowShareModal(true)}
                    className="flex items-center gap-2 text-[14px] font-medium text-[#1E1E1E] "
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M12.6438 5.49234L8.63333 2.02913C8.28228 1.72596 7.72901 1.97208 7.72901 2.44303V4.26715C4.06884 4.30906 1.1665 5.04262 1.1665 8.51131C1.1665 9.91134 2.06841 11.2983 3.06537 12.0234C3.37647 12.2497 3.81985 11.9657 3.70514 11.5989C2.67191 8.29457 4.19521 7.41734 7.72901 7.3665V9.36977C7.72901 9.84145 8.28272 10.0865 8.63333 9.78366L12.6438 6.32012C12.8961 6.10224 12.8965 5.71052 12.6438 5.49234Z" fill="#757575" />
                    </svg>
                    Share Task
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      </div>



      {/* Create Webhook Modal (shared with Settings → Webhooks) */}
      <CreateWebhookModal
        open={showCreateWebhook}
        name={newWebhookName}
        onNameChange={setNewWebhookName}
        creating={creatingWebhook}
        error={webhookError}
        onCreate={handleCreateWebhook}
        onClose={() => {
          setShowCreateWebhook(false);
          setNewWebhookName("");
        }}
      />
    </div>
  );
};

export default PromptDetailsForm;
